// Axios client used by the admin dashboard to call the ResidenOne API.
// Clerk supplies a short-lived session token for each request; the browser
// never stores or refreshes legacy-issued credentials.

import axios, { AxiosHeaders, type AxiosError } from 'axios';
import { getClerkToken } from './clerk-auth';
import { apiBaseUrl } from '../runtime-config';

const SELECTED_CLUSTER_KEY = 'residenone:selected-cluster-id';

export function getSelectedClusterId(): string | null {
  return window.localStorage.getItem(SELECTED_CLUSTER_KEY);
}

export function setSelectedClusterId(id: string): void {
  window.localStorage.setItem(SELECTED_CLUSTER_KEY, id);
}

export function clearSelectedClusterId(): void {
  window.localStorage.removeItem(SELECTED_CLUSTER_KEY);
}

export const api = axios.create({
  baseURL: apiBaseUrl,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(async (config) => {
  const token = await getClerkToken();
  if (token) {
    if (!(config.headers instanceof AxiosHeaders)) config.headers = new AxiosHeaders(config.headers);
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  const clusterId = getSelectedClusterId();
  if (clusterId && !config.headers?.has('X-Cluster-ID')) {
    config.headers.set('X-Cluster-ID', clusterId);
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new CustomEvent('residenone:auth:logout'));
    }
    return Promise.reject(error);
  },
);

export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> };
}

export function extractApiError(err: unknown): { code?: string; message: string } {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    if (body?.error) return { code: body.error.code, message: body.error.message };
    return { message: err.message };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: 'Unknown error' };
}

export function extractApiErrorDetails(err: unknown): Record<string, unknown> | undefined {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    return body?.error?.details;
  }
  return undefined;
}
